import { useState } from "react";
import { Download, Pencil, Trash2, Lock, RefreshCw, AlertTriangle, MessageSquare } from "lucide-react";
import SwipeCard from "@/components/SwipeCard";
import TripChat from "@/components/TripChat";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import OptionPhoto from "@/components/OptionPhoto";
import CustomerPriceEditor from "@/components/CustomerPriceEditor";
import { FlightTime } from "@/components/FlightTime";
import { Checkbox } from "@/components/ui/checkbox";
import { categoryLabel, legsCoveredLabel, money } from "@/lib/format";
import { OPTION_STATUS, OptionStatusActions, OptionStatusBadge } from "@/components/OptionStatus";

function Spec({ label, value }) {
  return (
    <div>
      <dt className="text-[0.58rem] tracking-[0.22em] uppercase text-zinc-500">{label}</dt>
      <dd className="text-sm text-zinc-100 mt-1 break-words">{value || "—"}</dd>
    </div>
  );
}

function DeleteButton({ o, onDelete }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button className="p-2 text-zinc-500 hover:text-rose-300 transition-colors" data-testid={`option-${o.rank}-delete-btn`} aria-label="Delete option"><Trash2 size={15} /></button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[#111319] border-white/10">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-serif-display text-2xl font-normal">Remove this option?</AlertDialogTitle>
          <AlertDialogDescription>{o.aircraft_type} from {o.operator_name} will be removed and the broker will be notified.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="option-delete-cancel-btn">Keep</AlertDialogCancel>
          <AlertDialogAction onClick={() => onDelete(o)} className="bg-rose-600 hover:bg-rose-500 text-white" data-testid="option-delete-confirm-btn">Remove</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function OptionCard({ o, staff, showInternal = staff, readOnly = false, jetCard, selected, picked, onPick, onEdit, onDelete, onPdf, onCustomerPdf, onRefresh, onPhotosChanged, onTripChange, trip, qCount = 0, onAsked }) {
  const [asking, setAsking] = useState(false);
  const lowest = o.rank === 1;
  const over = !!o.over_budget;
  const st = OPTION_STATUS[o.status];
  const accent = st && o.status !== "not_selected" && o.status !== "unavailable" ? st.color : null;
  const right = onPick ? { label: picked ? "Unselect" : "Select", action: () => onPick(o) } : null;
  return (
    <SwipeCard right={right} left={null} testId={`option-${o.rank}-swipe`}>
    <article className={`rise lux-card ${accent ? "ring-1" : over ? "!border-rose-400 ring-1 ring-rose-400/40 bg-rose-500/[0.03]" : selected ? "!border-[#5B7FD1] ring-1 ring-[#5B7FD1]/40" : lowest ? "!border-[#5B7FD1]/40" : ""}`}
      style={{ animationDelay: `${o.rank * 60}ms`, ...(accent ? { borderColor: accent, boxShadow: `inset 4px 0 0 ${accent}`, "--tw-ring-color": `${accent}55` } : {}) }}
      data-testid={`option-card-${o.rank}`}>
      <div className={`grid lg:grid-cols-[1fr_minmax(0,1.05fr)] transition-opacity ${["not_selected", "unavailable"].includes(o.status) ? "opacity-60 grayscale-[35%]" : ""}`} data-testid={`option-${o.rank}-body`}>
        <div className="p-5 sm:p-7 flex flex-col">
          <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
            {onPick && (
              <Checkbox checked={picked} onCheckedChange={() => onPick(o)} className="mt-0.5 h-5 w-5 border-white/30 data-[state=checked]:bg-[#1E3A8A] data-[state=checked]:border-[#1E3A8A] data-[state=checked]:text-white"
                aria-label={`Select option ${o.rank} for PDF`} data-testid={`option-${o.rank}-pick-checkbox`} />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-mono-data text-[11px] text-[#5B7FD1]" data-testid={`option-${o.rank}-rank`}>
                OPTION {String(o.rank).padStart(2, "0")}{lowest ? " · LOWEST PRICE" : ""}{selected ? " · CLIENT SELECTED" : ""}
              </p>
              {st && <div className="mt-2"><OptionStatusBadge o={o} /></div>}
              {over && (
                <p className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 text-[0.62rem] tracking-[0.16em] uppercase bg-rose-600 text-white" data-testid={`option-${o.rank}-over-budget`}>
                  <AlertTriangle size={12} /> Over budget by {money(o.over_by, o.currency)}
                </p>
              )}
              <h3 className="font-serif-display text-2xl sm:text-3xl text-white mt-2 leading-tight break-words">{o.aircraft_type}</h3>
              <p className="text-zinc-400 text-xs mt-1 tracking-wide">{categoryLabel(o.aircraft_category)} · {o.tail_number || "Tail TBA"}</p>
              {legsCoveredLabel(o.legs_covered, trip?.legs) && (
                <p className="mt-2 inline-block px-2.5 py-1 text-[0.62rem] tracking-[0.14em] uppercase border border-[#5B7FD1]/50 text-[#9DB4EA]" data-testid={`option-${o.rank}-legs`}>{legsCoveredLabel(o.legs_covered, trip?.legs)}</p>
              )}
            </div>
            <div className="sm:text-right">
              <p className="text-[0.58rem] tracking-[0.2em] uppercase text-zinc-500">Cost from operator</p>
              <p className="font-serif-display text-2xl sm:text-3xl text-white whitespace-nowrap" data-testid={`option-${o.rank}-price`}>{money(o.price, o.currency)}</p>
              <p className="text-[0.58rem] tracking-[0.2em] uppercase text-zinc-500 mt-2">{jetCard ? "Price to customer · Jet Card budget" : "Price to customer"}</p>
              <div>
                {jetCard ? (
                  <p className="font-serif-display text-xl text-[#5B7FD1] whitespace-nowrap" data-testid={`option-${o.rank}-customer-price`}>
                    {o.customer_price ? money(o.customer_price, o.currency) : <span className="font-sans text-xs tracking-[0.15em] uppercase text-zinc-500">Set the trip budget</span>}
                  </p>
                ) : readOnly ? (
                  <p className="font-serif-display text-xl text-[#5B7FD1] whitespace-nowrap" data-testid={`option-${o.rank}-customer-price`}>
                    {o.customer_price ? money(o.customer_price, o.currency) : <span className="font-sans text-xs tracking-[0.15em] uppercase text-zinc-500">Not set</span>}
                  </p>
                ) : <CustomerPriceEditor o={o} onSaved={onPhotosChanged} />}
              </div>
            </div>
          </div>
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-5 gap-y-4 mt-7">
            <Spec label="Operator" value={o.operator_name} />
            {o.operator_certificate && <Spec label="Certificate" value={o.operator_certificate} />}
            <Spec label="Tail number" value={o.tail_number} />
            <Spec label="YOM" value={o.year} />
            <Spec label="YOR" value={o.year_refurbished} />
            <Spec label="Seats" value={o.seats} />
            <Spec label="Est. flight time" value={o.flight_time && <FlightTime value={o.flight_time} testId={`option-${o.rank}-flight-time`} />} />
          </dl>
          {o.amenities && <p className="text-xs text-zinc-400 mt-5"><span className="text-zinc-500 uppercase tracking-[0.2em] text-[0.58rem] mr-2">Amenities</span>{o.amenities}</p>}
          {o.notes && <p className="text-sm text-zinc-300 mt-4 leading-relaxed">{o.notes}</p>}
          {showInternal && o.internal_notes && (
            <p className="text-xs text-amber-200/80 mt-4 flex gap-2 bg-amber-500/5 border border-amber-500/15 p-3" data-testid={`option-${o.rank}-internal-notes`}>
              <Lock size={12} className="mt-0.5 shrink-0" /> {o.internal_notes}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-auto pt-6">
            <button className="frame-btn !py-2.5 !px-4" onClick={() => onPdf(o)} data-testid={`option-${o.rank}-pdf-btn`}><Download size={13} /> Option PDF</button>
            {o.customer_price && onCustomerPdf && (
              <button className="frame-btn !py-2.5 !px-4" onClick={() => onCustomerPdf([o])} data-testid={`option-${o.rank}-customer-pdf-btn`}><Download size={13} /> Customer PDF</button>
            )}
            {trip && (
              <button className={`frame-btn !py-2.5 !px-4 ${asking ? "gold" : ""}`} onClick={() => setAsking((a) => !a)} data-testid={`option-${o.rank}-ask-btn`}>
                <MessageSquare size={13} /> {staff || readOnly ? `Messages${qCount ? ` (${qCount})` : ""}` : `Ask a question${qCount ? ` (${qCount})` : ""}`}
              </button>
            )}
            {trip && onTripChange && <OptionStatusActions o={o} trip={trip} staff={staff} onChanged={onTripChange} />}
            {staff && (
              <>
                <button className="p-2 text-zinc-500 hover:text-[#5B7FD1] transition-colors ml-auto" onClick={() => onRefresh(o)} data-testid={`option-${o.rank}-refresh-photos-btn`} aria-label="Refresh photos" title="Search photos again"><RefreshCw size={15} /></button>
                <button className="p-2 text-zinc-500 hover:text-white transition-colors" onClick={() => onEdit(o)} data-testid={`option-${o.rank}-edit-btn`} aria-label="Edit option"><Pencil size={15} /></button>
                <DeleteButton o={o} onDelete={onDelete} />
              </>
            )}
          </div>
          {asking && trip && <TripChat trip={trip} option={o} onChanged={onAsked} />}
        </div>
        <div className="grid grid-cols-2 gap-px bg-white/[0.06] border-t lg:border-t-0 lg:border-l border-white/[0.06]">
          <OptionPhoto option={o} kind="exterior" label="Exterior" staff={staff} onChanged={onPhotosChanged} testId={`option-${o.rank}-exterior-photo`} />
          <OptionPhoto option={o} kind="interior" label="Interior" staff={staff} onChanged={onPhotosChanged} testId={`option-${o.rank}-interior-photo`} />
        </div>
      </div>
    </article>
    </SwipeCard>
  );
}
